import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { callErrorMsg } from 'helpers/errorMsg'
import { AUTH_ADMIN } from 'middleware/privateRoute'
import { getInventoryStatusNotes } from 'features/Product/services/product'

import { postInventoryDetailTransfer } from 'features/Product/services/inventoryDetail'
import {
  getInventoryLocationAPI,
  getCheckPackageIdAPI,
  getInventoryStatusAPI,
  getHubLocationsAPI,
  getWarehouseLocationsAPI,
} from 'utils/apiList/productTanggungRenteng'
import type {
  GetCheckPackageIdRequestType,
  GetCheckPackageIdResponseType,
  GetInventoryLocationAPIRequestType,
  GetInventoryLocationAPIResponseType,
  GetInventoryStatusResponseType,
  GetHubLocationsResponseType,
  GetWarehouseLocationsResponseType,
  PutTransferProductDetailRequestType,
} from 'utils/apiList/productTanggungRenteng'
import dayjs from 'dayjs'
import { InventoryStatusNotesItemType } from 'features/Product/@types/Product'
import { serializeQueryToURL } from 'utils/queryParamsURL'
import { toastSuccess } from 'utils/toast'

export const SLICE_NAME = 'hubProductTanggungRenteng'

export const setQuery = createAction<GetInventoryLocationAPIRequestType>(`${SLICE_NAME}/setQuery`)
export const actSetQuery =
  (query: GetInventoryLocationAPIRequestType) => (dispatch: StoreDispatchType) => {
    dispatch(setQuery(query))
    dispatch(fetchGetProductStockList(query))
  }

export const fetchGetProductStockList = createAsyncThunk<
  GetInventoryLocationAPIResponseType,
  GetInventoryLocationAPIRequestType,
  RejectValueType
>(`${SLICE_NAME}/fetchGetProductStockList`, async (params, { rejectWithValue }) => {
  try {
    const qs = serializeQueryToURL(params)
    const response = await getInventoryLocationAPI(qs)

    return response.data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchGetPackageLabel = createAsyncThunk<
  GetCheckPackageIdResponseType,
  GetCheckPackageIdRequestType,
  RejectValueType
>(`${SLICE_NAME}/fetchGetPackageLabel`, async (params, { rejectWithValue }) => {
  try {
    const response = await getCheckPackageIdAPI(params)

    return response.data
  } catch (error) {
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchGetCheckPackageId = createAsyncThunk<
  GetCheckPackageIdResponseType,
  GetCheckPackageIdRequestType,
  RejectValueType
>(`${SLICE_NAME}/fetchGetCheckPackageId`, async (params, { rejectWithValue }) => {
  try {
    const response = await getCheckPackageIdAPI(params)

    return response.data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchGetInventoryStatus = createAsyncThunk<
  GetInventoryStatusResponseType,
  undefined,
  RejectValueType
>(`${SLICE_NAME}/fetchGetInventoryStatus`, async (_, { rejectWithValue }) => {
  try {
    const response = await getInventoryStatusAPI()

    return response.data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchGetInventoryStatusNotes = createAsyncThunk<
  InventoryStatusNotesItemType[],
  void,
  RejectValueType
>(`${SLICE_NAME}/fetchGetInventoryStatusNotes`, async (_, { getState, rejectWithValue }) => {
  try {
    const {
      productTanggungRenteng: {
        modalUpdate: {
          constants: { status },
        },
      },
    } = getState() as never
    const statusList = status as GetInventoryStatusResponseType['data']['status']

    if (!statusList?.length) {
      return [] as InventoryStatusNotesItemType[]
    }

    const listStatusId = statusList.map((item) => item.id)

    const results = await Promise.all(
      listStatusId.map((id) =>
        getInventoryStatusNotes({
          page_size: 100,
          page_index: 1,
          inventory_status_id: id,
          exclusion_preset: 0,
        }).then((responseNotes) =>
          responseNotes.data.data.map((item) => ({ ...item, status_id: id })),
        ),
      ),
    )
    return results.flat()
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchPutTransferProductDetail = createAsyncThunk<
  void,
  PutTransferProductDetailRequestType,
  RejectValueType
>(
  `${SLICE_NAME}/fetchPutTransferProductDetail`,
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const mappedPayload = {
        inventory_detail_id: id,
        stock: Number(payload.stock),
        status_id: payload.statusId as number,
        ...(payload.statusNotesId !== undefined ? { status_note_id: payload.statusNotesId } : {}),
        ...(payload.rackId !== undefined ? { rack_id: payload.rackId } : {}),
        ...(payload.packageId !== undefined ? { package_id: payload.packageId } : {}),
        ...(payload.expiryDate !== undefined
          ? { expiry_date: dayjs(payload.expiryDate).format('YYYY-MM-DD') }
          : {}),
      }

      const response = await postInventoryDetailTransfer(mappedPayload)

      dispatch(
        fetchGetProductStockList({
          locationId: payload.locationId,
          pageIndex: 1,
        }),
      )

      toastSuccess(response.data.data.message)
      return undefined
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetHubLocations = createAsyncThunk<
  GetHubLocationsResponseType['data'],
  undefined,
  RejectValueType
>(`${SLICE_NAME}/fetchGetHubLocations`, async (_, { rejectWithValue, getState }) => {
  try {
    const {
      auth: { userData },
    } = getState() as never

    const response = await getHubLocationsAPI()
    const { data } = response.data

    const isAdmin = (userData as { authorities: { name: string }[] }).authorities.some(
      (item) => item.name === AUTH_ADMIN,
    )
    const listUserLocationId = (
      userData as { location_roles: { location_id: number }[] }
    ).location_roles.map((item) => item.location_id)

    return isAdmin ? data : data.filter((item) => listUserLocationId.includes(item.location_id))
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

export const fetchGetWarehouseLocations = createAsyncThunk<
  GetWarehouseLocationsResponseType,
  undefined,
  RejectValueType
>(`${SLICE_NAME}/fetchGetWarehouseLocations`, async (_, { rejectWithValue, getState }) => {
  try {
    const {
      auth: { userData },
    } = getState() as never

    const response = await getWarehouseLocationsAPI()
    const { data } = response

    const isAdmin = (userData as { authorities: { name: string }[] }).authorities.some(
      (item) => item.name === AUTH_ADMIN,
    )
    const listUserLocationId = (
      userData as { location_roles: { location_id: number }[] }
    ).location_roles.map((item) => item.location_id)

    return isAdmin ? data : data.filter((item) => listUserLocationId.includes(item.location_id))
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})
